import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CloudLightning, MapPin, Zap, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CanvassingStormTriggerProps {
  onCreateTerritory: (t: {
    name: string;
    description?: string;
    center_lat: number;
    center_lng: number;
    radius_miles?: number;
    storm_triggered?: boolean;
    storm_date?: string;
  }) => Promise<any>;
}

interface StormAlert {
  location: string;
  condition: string;
  wind_mph: number;
  hailRisk: "Low" | "Med" | "High";
  temp_f: number;
}

export function CanvassingStormTrigger({ onCreateTerritory }: CanvassingStormTriggerProps) {
  const [location, setLocation] = useState("");
  const [checking, setChecking] = useState(false);
  const [stormData, setStormData] = useState<StormAlert | null>(null);
  const [weatherRaw, setWeatherRaw] = useState<any>(null);
  const [creating, setCreating] = useState(false);

  const checkWeather = async () => {
    if (!location.trim()) return;
    setChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-weather-forecast", {
        body: { location: location.trim(), days: 3 },
      });
      if (error) throw error;

      setWeatherRaw(data);
      const condition = data.current.condition.toLowerCase();
      const wind = data.current.wind_mph;

      let hailRisk: "Low" | "Med" | "High" = "Low";
      if ((condition.includes("storm") || condition.includes("thunder")) && wind > 20) hailRisk = "High";
      else if (condition.includes("rain") && wind > 15) hailRisk = "Med";

      setStormData({
        location: `${data.location.name}, ${data.location.region}`,
        condition: data.current.condition,
        wind_mph: wind,
        hailRisk,
        temp_f: data.current.temp_f,
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to check weather");
    } finally {
      setChecking(false);
    }
  };

  const handleCreateStormTerritory = async () => {
    if (!stormData || !weatherRaw) return;
    setCreating(true);

    // Use a geocoding approach from the weather data (lat/lng not in response, so estimate from location)
    // For now, get user's current location as fallback
    navigator.geolocation?.getCurrentPosition(
      async (pos) => {
        await onCreateTerritory({
          name: `⚡ Storm Zone - ${stormData.location}`,
          description: `Auto-generated from ${stormData.hailRisk} hail risk alert. ${stormData.condition}, ${Math.round(stormData.wind_mph)} mph winds.`,
          center_lat: pos.coords.latitude,
          center_lng: pos.coords.longitude,
          radius_miles: stormData.hailRisk === "High" ? 5 : 3,
          storm_triggered: true,
          storm_date: new Date().toISOString().split("T")[0],
        });
        setCreating(false);
      },
      async () => {
        // Fallback with approximate coords
        await onCreateTerritory({
          name: `⚡ Storm Zone - ${stormData.location}`,
          description: `Auto-generated from ${stormData.hailRisk} hail risk alert. ${stormData.condition}, ${Math.round(stormData.wind_mph)} mph winds.`,
          center_lat: 41.88,
          center_lng: -87.63,
          radius_miles: stormData.hailRisk === "High" ? 5 : 3,
          storm_triggered: true,
          storm_date: new Date().toISOString().split("T")[0],
        });
        setCreating(false);
      }
    );
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <CloudLightning className="w-5 h-5 text-yellow-500" /> Storm-Triggered Canvassing
      </h2>

      <Alert className="border-yellow-500/30 bg-yellow-500/5">
        <Zap className="w-4 h-4 text-yellow-500" />
        <AlertDescription className="text-sm">
          Check weather conditions in any area. When hail or severe storms are detected, instantly generate a canvassing territory to deploy your team.
        </AlertDescription>
      </Alert>

      {/* Location check */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Check Storm Conditions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Enter city, zip, or address..."
              value={location}
              onChange={e => setLocation(e.target.value)}
              onKeyDown={e => e.key === "Enter" && checkWeather()}
            />
            <Button onClick={checkWeather} disabled={checking || !location.trim()}>
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {stormData && (
        <Card className={stormData.hailRisk === "High"
          ? "border-red-500/30 bg-red-500/5"
          : stormData.hailRisk === "Med"
            ? "border-yellow-500/30 bg-yellow-500/5"
            : "border-green-500/30 bg-green-500/5"
        }>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{stormData.location}</p>
                <p className="text-2xl font-bold text-foreground">{Math.round(stormData.temp_f)}°F</p>
                <p className="text-sm text-muted-foreground">{stormData.condition}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">Hail Risk</p>
                <Badge
                  variant={stormData.hailRisk === "High" ? "destructive" : stormData.hailRisk === "Med" ? "secondary" : "default"}
                  className="text-sm px-3 py-1"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                  {stormData.hailRisk}
                </Badge>
                <p className="text-xs text-muted-foreground mt-1">{Math.round(stormData.wind_mph)} mph wind</p>
              </div>
            </div>

            {(stormData.hailRisk === "High" || stormData.hailRisk === "Med") && (
              <Button
                onClick={handleCreateStormTerritory}
                disabled={creating}
                className="w-full gap-2"
                variant={stormData.hailRisk === "High" ? "destructive" : "default"}
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <MapPin className="w-4 h-4" />
                )}
                Deploy Canvassing Team to {stormData.location}
              </Button>
            )}

            {stormData.hailRisk === "Low" && (
              <p className="text-sm text-muted-foreground text-center">
                Low risk — no storm canvassing recommended at this time
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
